/**
 * SampleQuestionsPanel Component
 * 
 * Column 3 of CustomRoundModal
 * Displays generated sample questions preview
 * Contains primary actions: Cancel and Add/Update Round (moved from Column 2)
 */

import React, { useMemo } from "react";
import { 
  Typography, 
  Spin, 
  Button,
  Radio,
  Tag,
  Alert,
} from "antd";
import { QuestionCircleOutlined, LoadingOutlined, ExclamationCircleOutlined, ExperimentOutlined } from "@ant-design/icons";
import { SampleQuestionsPanelProps } from "../types";
import { SampleQuestion } from "../../../../../../../Interface";
import { SHARED_STYLES, COLUMN_STYLES, ColumnStatus } from "../constants";
import ColumnProgressIndicator from "./ColumnProgressIndicator";

const { Text } = Typography;

/**
 * QuestionCard - Renders a single sample question
 */
const QuestionCard: React.FC<{ question: SampleQuestion; index: number }> = ({ 
  question, 
  index 
}) => {
  return (
    <div style={{ marginBottom: 12, wordWrap: "break-word", overflowWrap: "break-word" }}>
      <Text 
        style={{ 
          wordWrap: "break-word", 
          whiteSpace: "normal", 
          display: "block",
          fontSize: 13,
        }}
      >
        <Text type="secondary" style={{ marginRight: 4 }}>{index + 1}.</Text>
        {question.question}
      </Text>

      {question.answerType === "mcq" && question.options && (
        <div style={{ marginTop: 6, marginLeft: 16 }}>
          {question.options.map((option, optionIndex) => (
            <div key={optionIndex} style={{ marginBottom: 2, wordWrap: "break-word" }}>
              <Radio 
                disabled 
                value={option} 
                style={{ whiteSpace: "normal", wordWrap: "break-word", fontSize: 12 }}
              >
                {option}
              </Radio>
            </div>
          ))}
        </div>
      )}

      {question.answerType === "descriptive" && (
        <Tag color="blue" style={{ marginTop: 6, fontSize: 11 }}>Descriptive</Tag>
      )}

      {question.answerType === "coding" && (
        <Tag color="purple" style={{ marginTop: 6, fontSize: 11 }}>Coding</Tag>
      )}
    </div>
  );
};

const SampleQuestionsPanel: React.FC<SampleQuestionsPanelProps> = ({
  isLoading,
  questions,
  hasFormChangedAfterGeneration,

  // Primary actions (moved from Column 2)
  isFormFilled,
  isEditing,
  isSubmitting,
  settingsOnlyMode,
  onCancel,
  onSubmit,
  isSimulationRound = false,
}) => {
  // Compute column progress status
  const columnStatus: ColumnStatus = useMemo(() => {
    // Completed when questions are loaded
    if (questions.length > 0) return "completed";
    // In progress when loading
    if (isLoading) return "inProgress";
    // Todo when no questions yet
    return "todo";
  }, [questions, isLoading]);

  return (
    <div
      style={{
        ...COLUMN_STYLES.base,
        ...COLUMN_STYLES.column3,
      }}
    >
      {/* Header with Progress Indicator */}
      <div style={COLUMN_STYLES.headerWrapper}>
        <div style={COLUMN_STYLES.header}>
          <Text strong style={{ fontSize: 16 }}>
            {isSimulationRound ? (
              <><ExperimentOutlined style={{ marginRight: 8, color: SHARED_STYLES.primaryColor }} />Simulation</>
            ) : (
              <><QuestionCircleOutlined style={{ marginRight: 8, color: SHARED_STYLES.primaryColor }} />Sample Questions</>
            )}
          </Text>
          <Text type="secondary" style={{ display: "block", fontSize: SHARED_STYLES.fontSizeSm }}>
            {isSimulationRound ? "AI-powered scenario assessment" : "Preview AI-generated questions"}
          </Text>
        </div>
        {!isSimulationRound && <ColumnProgressIndicator status={columnStatus} />}
      </div>

      {/* Scrollable Content */}
      <div
        className="custom-round-modal-scrollable"
        style={COLUMN_STYLES.scrollableContent}
      >
        {isSimulationRound ? (
          // Simulation info
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              padding: "40px 20px",
              textAlign: "center",
            }}
          >
            <ExperimentOutlined
              style={{
                fontSize: 48,
                color: SHARED_STYLES.primaryColor,
                marginBottom: 16,
              }}
            />
            <Text strong style={{ fontSize: 16, marginBottom: 8 }}>
              Simulation Assessment
            </Text>
            <Text
              type="secondary"
              style={{ fontSize: 13, maxWidth: 260, lineHeight: 1.6 }}
            >
              Candidates will be placed in a realistic, AI-driven scenario tailored to the job role. The simulation blueprint will be auto-configured when the job post is published.
            </Text>
          </div>
        ) : (
          <>
          {/* Form Changed Alert */}
          {questions.length > 0 && hasFormChangedAfterGeneration && (
            <Alert
              type="warning"
              message="Configuration Changed"
              description="Round configuration has changed since these questions were generated. Use the button in Column 2 to regenerate."
              showIcon
              icon={<ExclamationCircleOutlined />}
              style={{ marginBottom: SHARED_STYLES.marginMd }}
            />
          )}

          {isLoading ? (
            // Loading State
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                padding: "40px 20px",
              }}
            >
              <Spin indicator={<LoadingOutlined spin />} size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary" style={{ wordWrap: "break-word" }}>
                  Generating sample questions...
                </Text>
              </div>
            </div>
          ) : questions.length > 0 ? (
            // Questions List
            <div
              style={{
                padding: SHARED_STYLES.paddingMd,
                border: SHARED_STYLES.border,
                borderRadius: SHARED_STYLES.borderRadius,
                backgroundColor: SHARED_STYLES.containerBackground,
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {questions.map((question, index) => (
                <QuestionCard key={question.id} question={question} index={index} />
              ))}
            </div>
          ) : (
            // Empty State
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                padding: "40px 20px",
                textAlign: "center",
              }}
            >
              <QuestionCircleOutlined
                style={{
                  fontSize: 48,
                  color: SHARED_STYLES.textQuaternary,
                  marginBottom: 16,
                }}
              />
              <Text
                type="secondary"
                style={{ fontSize: 14, wordWrap: "break-word", maxWidth: 220 }}
              >
                Configure round details and generate sample questions using the button in Column 2
              </Text>
            </div>
          )}
          </>
        )}
      </div>

      {/* Footer - Primary Actions */}
      <div
        style={{
          ...COLUMN_STYLES.footer,
          display: "flex",
          gap: 12,
        }}
      >
        <Button 
          size="large" 
          onClick={onCancel}
          disabled={isSubmitting}
          style={{ flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={onSubmit}
          loading={isSubmitting}
          disabled={!isFormFilled}
          style={{ flex: 2 }}
        >
          {isEditing ? "Update Round" : settingsOnlyMode ? "Save Settings" : "Add Round"}
        </Button>
      </div>
    </div>
  );
};

export default SampleQuestionsPanel;

